<script setup lang="ts">
/**
 * Per-resource logging for both families, in one editable grid.
 *
 * The only editable grid in Monitoring, and therefore the only one built on `Spreadsheet` rather
 * than `DataTable`. The workflow it exists for is "raise these six AvaTax egresses to `full` while I
 * debug, then put them back" — six edits with a plain table, one edit and a drag of the fill handle
 * with a spreadsheet.
 */
import { computed, onMounted, ref } from 'vue'
import {
  Spreadsheet,
  ToggleSwitch,
  createChangeset,
  setCellError,
  type SpreadsheetColumn,
  type SpreadsheetRow,
} from '@hantera/design-system'
import { useLocalization } from '@hantera/portal-app'

import type { ResourceLogging } from '@/backend/ingresses.types'
import type { LoggingRow, ResourceFamily } from './logging-view-context'
import useLoggingViewContext from './use-logging-view-context'

const t = useLocalization('monitoring')

const logging = useLoggingViewContext()


const changeset = createChangeset()
const saveError = ref<string | null>(null)

const LOGGING_FIELDS = ['logLevel', 'logRetentionDays', 'logFullRetentionDays'] as const

const rows = computed<SpreadsheetRow[]>(() =>
  logging.filteredRows.map((row: LoggingRow) => ({
    // Family-qualified: an ingress and an egress may legitimately share an id, and a duplicate row
    // key would make an edit to one appear on the other.
    key: `${row.family}:${row.resourceId}`,
    values: {
      resource: row.resourceId,
      family: row.family,
      logLevel: row.logLevel ?? null,
      logRetentionDays: row.logRetentionDays ?? null,
      logFullRetentionDays: row.logFullRetentionDays ?? null,
    },
  })),
)

function rowFor(key: string): LoggingRow | undefined {
  const [family, ...rest] = key.split(':')
  const resourceId = rest.join(':')
  return logging.rows.find(
    (row) => row.family === (family as ResourceFamily) && row.resourceId === resourceId,
  )
}

/**
 * `editable` is a property of the column, not the cell, so a grid mixing writable and read-only
 * resources cannot express the difference through `editable` alone. Rejecting in `validate` explains
 * why instead of letting the cell silently refuse to open.
 */
function rejectIfReadOnly(row: SpreadsheetRow): string | undefined {
  return rowFor(row.key)?.canWrite ? undefined : t('settings.readOnlyRow')
}

/**
 * An hours ceiling as the largest whole day the grid may accept.
 *
 * Rounded **up**, so a sub-day ceiling still admits `1` — the smallest value a day-valued column can
 * express. The server then clamps that request down to the real ceiling rather than rejecting it,
 * which is what keeps the column usable on a tenant capped at an hour.
 */
function ceilingInDays(hours: number | undefined): number | undefined {
  return hours == null ? undefined : Math.max(1, Math.ceil(hours / 24))
}

function boundedRetention(max: number | undefined, field: string) {
  return (value: any, row: SpreadsheetRow): string | undefined => {
    const readOnly = rejectIfReadOnly(row)
    if (readOnly) return readOnly

    if (value == null) return undefined

    if (!Number.isInteger(value) || value < 1) {
      return t('settings.retentionWholeDays', { field })
    }

    // When the ceiling could not be read, the server remains the authority and its
    // 400 INVALID_ARGUMENT names the limit.
    if (max != null && value > max) {
      return t('settings.retentionRange', { field, max })
    }

    return undefined
  }
}

const columns = computed<SpreadsheetColumn[]>(() => [
  { key: 'resource', label: t('settings.columns.resource'), type: 'text' },
  {
    key: 'family',
    label: t('settings.columns.family'),
    type: 'enum',
    enumValues: [{ key: 'ingress' }, { key: 'egress' }],
  },
  {
    key: 'logLevel',
    label: t('settings.columns.level'),
    type: 'enum',
    editable: true,
    // `full` is hued: it is the level that persists payloads, so it must be identifiable by
    // scanning the column rather than by reading every cell.
    enumValues: [{ key: 'off' }, { key: 'metadata' }, { key: 'full', hue: 25 }],
    validate: (_value: any, row: SpreadsheetRow) => rejectIfReadOnly(row),

  },
  {
    key: 'logRetentionDays',
    label: t('settings.columns.retention'),
    type: 'number',
    editable: true,
    min: 1,
    max: ceilingInDays(logging.ceilings.maxMetadataHours),
    unitLabel: ' d',
    maximumFractionDigits: 0,
    validate: boundedRetention(ceilingInDays(logging.ceilings.maxMetadataHours), 'logRetentionDays'),
  },
  {
    key: 'logFullRetentionDays',
    label: t('settings.columns.bodyRetention'),
    type: 'number',
    editable: true,
    min: 1,
    max: ceilingInDays(logging.ceilings.maxFullHours),
    unitLabel: ' d',
    maximumFractionDigits: 0,
    validate: boundedRetention(ceilingInDays(logging.ceilings.maxFullHours), 'logFullRetentionDays'),
  },
])

/**
 * A ceiling as text, in whichever unit states it without lying.
 *
 * The columns are in days, so a ceiling of less than one day cannot be shown as a day figure — a
 * tenant capped at an hour would otherwise read "max 1 days" while their entry of `1` silently became
 * an hour, which looks like a bug rather than a limit.
 */
function ceilingLabel(hours: number | undefined): string {
  if (hours == null) return '?'

  return hours < 24
    ? t('settings.ceilingHours', { count: hours })
    : t('settings.ceilingDays', { count: Math.floor(hours / 24) })
}

const defaultsHint = computed(() => {
  const { defaultLevel, maxMetadataHours, maxFullHours } = logging.ceilings
  if (!defaultLevel) return ''

  return t('settings.defaultsHint', {
    level: defaultLevel,
    metadata: ceilingLabel(maxMetadataHours),
    full: ceilingLabel(maxFullHours),
  })
})

const dirtyRowCount = computed(() => changeset.cells.size)

/**
 * One PATCH per dirty row, carrying only the cells that moved.
 *
 * Per-cell autosave was rejected: one drag of the fill handle dirties a dozen cells at once, and
 * raising a resource to `full` starts persisting payloads — not a change to make by accident.
 */
async function save() {
  saveError.value = null
  logging.setSaving(true)

  const failures: string[] = []

  const dirtyRows: [string, Map<string, { value: any }>][] = Array.from(changeset.cells.entries())

  for (const [rowKey, cells] of dirtyRows) {

    const row = rowFor(rowKey)
    if (!row) continue

    if (!row.canWrite) {
      for (const columnKey of cells.keys()) {
        setCellError(changeset, rowKey, columnKey, t('settings.readOnlyRow'))
      }
      failures.push(row.resourceId)
      continue
    }

    const patch: ResourceLogging = {}
    for (const field of LOGGING_FIELDS) {
      const change = cells.get(field)
      if (change) {
        // Explicit null is meaningful: the API reads it as "clear the override".
        ; (patch as any)[field] = change.value ?? null
      }
    }

    try {
      await logging.save(row.resourceId, row.family, patch)
      changeset.cells.delete(rowKey)
    } catch (e: any) {
      failures.push(row.resourceId)

      // Mapped back onto the cell that caused it, rather than a toast that does not say which row
      // failed. The server is the authority even when client-side validation passed.
      const message = e?.message ?? t('settings.saveFailed')
      const [firstField] = Array.from(cells.keys())
      if (firstField) {
        setCellError(changeset, rowKey, firstField, message)
      }
    }
  }

  logging.setSaving(false)

  if (failures.length > 0) {
    saveError.value = t('settings.saveFailedFor', { resources: failures.join(', ') })
  }
}

function discard() {
  changeset.clear()
  saveError.value = null
}

onMounted(() => {
  logging.refresh()
})
</script>

<template>
  <div class="settings-tab">

    <div class="toggles">
      <input
        v-model="logging.filters.search"
        class="filter"
        type="search"
        :placeholder="t('settings.filterPlaceholder')"
      />

      <select v-model="logging.filters.family" class="family">
        <option value="all">{{ t('settings.allFamilies') }}</option>
        <option value="ingress">{{ t('settings.ingresses') }}</option>
        <option value="egress">{{ t('settings.egresses') }}</option>
      </select>

      <label class="overridden">
        <ToggleSwitch v-model="logging.filters.overriddenOnly" />
        {{ t('settings.overriddenOnly') }}
      </label>

      <span class="spacer" />

      <span v-if="defaultsHint" class="muted">{{ defaultsHint }}</span>
      <span v-else-if="logging.ceilingsUnavailable" class="muted">
        {{ t('settings.ceilingsUnavailable') }}
      </span>
    </div>

    <p class="warning">{{ t('settings.fullWarning') }}</p>

    <p v-if="logging.workState.error" class="error">{{ logging.workState.error }}</p>

    <!-- Rows are the tenant's resources: a resource cannot be created by typing a name into a
         logging grid, and removing a row would be ambiguous between clearing an override and
         deleting the resource. Delete on a cell clears the override, which is what the API already
         reads a null as. -->
    <Spreadsheet
      class="grid"
      :columns="columns"
      :rows="rows"
      :changeset="changeset"
      :allow-add-rows="false"
      :allow-remove-rows="false"
    />

    <div class="footer">
      <span v-if="saveError" class="error">{{ saveError }}</span>
      <span class="spacer" />
      <span v-if="changeset.hasChanges" class="muted">
        {{ t('settings.pendingChanges', { count: dirtyRowCount }) }}
      </span>
      <button
        class="btn"
        type="button"
        :disabled="!changeset.hasChanges || logging.workState.isSaving"
        @click="discard"
      >
        {{ t('settings.discard') }}
      </button>
      <button
        class="btn-primary"
        type="button"
        :disabled="!changeset.hasChanges || logging.workState.isSaving"
        @click="save"
      >
        {{ logging.workState.isSaving ? t('settings.saving') : t('settings.save') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.settings-tab {
  height: 100%;
  display: grid;
  grid-template-rows: min-content min-content auto min-content;
  min-height: 0;
}

.warning {
  margin: 0;
  padding: 6px 8px;
  border-radius: 4px;
  background-color: hsl(45 80% 94%);
  font-size: 12px;
}

.toggles {
  background-color: var(--surface);
  padding: var(--space-2);
}

.toggles,
.footer {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.footer {
  background-color: var(--background);
  padding: var(--space-2);
}

.filter {
  flex: 1;
  min-width: 180px;
  padding: 4px 8px;
  border: 1px solid hsl(var(--color-70));
  border-radius: 4px;
  background-color: hsl(var(--color-100));
  color: inherit;
  font-size: 12px;
}

.family {
  padding: 4px 6px;
  border: 1px solid hsl(var(--color-70));
  border-radius: 4px;
  background-color: hsl(var(--color-100));
  color: inherit;
  font-size: 12px;
}

.overridden {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--muted-foreground);
  white-space: nowrap;
}

.spacer {
  flex: 1;
}

.grid {
  min-height: 0;
  border-radius: 0;
}

.muted {
  color: var(--muted-foreground);
  font-size: 12px;
}

.error {
  color: hsl(var(--color-hue-red) 60% 45%);
  font-size: 12px;
}
</style>
